import React, { useEffect, useState, useCallback } from 'react';
import { Check, X, Trash, DeviceMobile, SignOut, CaretDown, UserPlus, Copy, ClockCounterClockwise, Warning } from '@phosphor-icons/react';

const ROLE_DESC = { admin: 'Full control', member: 'Regular use, needs approvals', child: 'Restricted' };

export const UsersTab = ({ api, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState({});
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'member' });
  const [invited, setInvited] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const loadActivity = async () => {
    setShowActivity(true);
    setActivityLoading(true);
    try {
      const res = await api.get('/auth/login-history?limit=100');
      setActivity(res.data);
    } catch (err) { console.error(err); }
    setActivityLoading(false);
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await fn(); load(); setSessions({}); }
    catch (err) { alert(err?.response?.data?.detail || 'Action failed'); }
  };

  const toggleSessions = async (userId) => {
    if (expanded === userId) { setExpanded(''); return; }
    setExpanded(userId);
    try {
      const res = await api.get(`/auth/users/${userId}/sessions`);
      setSessions(s => ({ ...s, [userId]: res.data }));
    } catch (err) { console.error(err); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/auth/users/invite', inviteForm);
      setInvited(res.data);
      setInviteForm({ name: '', email: '', role: 'member' });
      load();
    } catch (err) { alert(err?.response?.data?.detail || 'Invite failed'); }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(`HomeCommand login\nEmail: ${invited.email}\nTemporary password: ${invited.temp_password}\nPlease change your password in Settings after first login.`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>;
  const pending = users.filter(u => u.status === 'pending');
  const rest = users.filter(u => u.status !== 'pending');

  const deviceLabel = (d) => {
    if (!d) return 'Unknown device';
    if (/mobile|android|iphone/i.test(d)) return '📱 Mobile';
    if (/ipad|tablet/i.test(d)) return 'Tablet';
    return '💻 Desktop';
  };

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex justify-end gap-2">
        <button onClick={loadActivity} data-testid="login-activity-btn"
          className="px-4 py-2 rounded-full border border-border text-xs font-semibold hover:bg-muted flex items-center gap-2">
          <ClockCounterClockwise size={14} /> Login Activity
        </button>
        <button onClick={() => { setShowInvite(true); setInvited(null); }} data-testid="invite-user-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow flex items-center gap-2">
          <UserPlus size={14} /> Invite User
        </button>
      </div>
      {pending.length > 0 && (
        <div className="glass-card p-5 border-amber-500/30" data-testid="pending-users-panel">
          <p className="overline mb-3 text-amber-500">Awaiting Approval ({pending.length})</p>
          <div className="space-y-2">
            {pending.map(u => (
              <div key={u.user_id} className="flex items-center justify-between gap-3 rounded-xl bg-amber-500/5 border border-amber-500/20 px-4 py-3" data-testid={`pending-user-${u.user_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email} · registered {(u.created_at || '').slice(0, 10)}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => act(() => api.post(`/auth/users/${u.user_id}/approve`))} data-testid={`approve-user-${u.user_id}`}
                    className="px-3 py-1.5 rounded-full bg-[#22C55E]/10 text-[#22C55E] text-xs font-semibold hover:bg-[#22C55E]/20 flex items-center gap-1"><Check size={13} weight="bold" /> Approve</button>
                  <button onClick={() => act(() => api.delete(`/auth/users/${u.user_id}`), `Reject and delete ${u.name}'s account?`)} data-testid={`reject-user-${u.user_id}`}
                    className="px-3 py-1.5 rounded-full bg-red-500/10 text-red-500 text-xs font-semibold hover:bg-red-500/20 flex items-center gap-1"><X size={13} weight="bold" /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-muted-foreground">
              <th className="text-left px-4 py-3 font-medium">User</th>
              <th className="text-left px-4 py-3 font-medium">Role</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Last Login</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rest.map(u => {
              const isSelf = u.user_id === currentUser?.user_id;
              return (
                <React.Fragment key={u.user_id}>
                  <tr className="border-b border-border last:border-0 hover:bg-muted/20" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{u.name}{isSelf && <span className="text-[10px] text-[#FF6B35] ml-1.5">(you)</span>}</p>
                      <p className="text-xs text-muted-foreground">{u.email}{u.auth_provider === 'google' ? ' · Google' : ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select value={u.role} disabled={isSelf} data-testid={`role-select-${u.user_id}`}
                        onChange={e => act(() => api.put(`/auth/users/${u.user_id}/role`, { role: e.target.value }))}
                        title={ROLE_DESC[u.role]}
                        className="px-2 py-1.5 rounded-lg bg-muted/50 border border-border text-xs outline-none disabled:opacity-50 capitalize">
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="child">Child</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${u.status === 'disabled' ? 'bg-red-500/10 text-red-500' : 'bg-[#22C55E]/10 text-[#22C55E]'}`} data-testid={`user-status-${u.user_id}`}>
                        {u.status}
                      </span>
                      {u.temp_password && <span className="block text-[9px] text-amber-500 font-bold mt-0.5" title="Using temporary password">TEMP PWD</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{u.last_login ? u.last_login.slice(0, 16).replace('T', ' ') : '-'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => toggleSessions(u.user_id)} data-testid={`sessions-btn-${u.user_id}`} title="Active sessions"
                        className="p-1.5 rounded-lg hover:bg-muted text-blue-500 inline-flex items-center gap-1 text-xs">
                        <DeviceMobile size={14} /> {u.session_count}
                        <CaretDown size={10} className={`transition-transform ${expanded === u.user_id ? 'rotate-180' : ''}`} />
                      </button>
                      {!isSelf && (
                        <>
                          <button onClick={() => act(() => api.put(`/auth/users/${u.user_id}/status`, { status: u.status === 'disabled' ? 'active' : 'disabled' }), u.status !== 'disabled' ? `Disable ${u.name}? They will be logged out everywhere.` : null)}
                            data-testid={`toggle-status-${u.user_id}`} title={u.status === 'disabled' ? 'Activate' : 'Disable'}
                            className={`p-1.5 rounded-lg ml-1 ${u.status === 'disabled' ? 'text-[#22C55E] hover:bg-[#22C55E]/10' : 'text-amber-500 hover:bg-amber-500/10'}`}>
                            {u.status === 'disabled' ? <Check size={14} /> : <X size={14} />}
                          </button>
                          <button onClick={() => act(() => api.delete(`/auth/users/${u.user_id}`), `Permanently delete ${u.name}'s account?`)} data-testid={`delete-user-${u.user_id}`}
                            className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive ml-1"><Trash size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                  {expanded === u.user_id && (
                    <tr className="border-b border-border bg-muted/10">
                      <td colSpan={5} className="px-4 py-3" data-testid={`sessions-panel-${u.user_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase">Active Sessions</p>
                          {(sessions[u.user_id] || []).length > 0 && (
                            <button onClick={() => act(() => api.post(`/auth/users/${u.user_id}/logout-all`), `Log ${u.name} out of ALL devices?`)} data-testid={`logout-all-${u.user_id}`}
                              className="text-xs font-semibold text-red-500 hover:underline flex items-center gap-1"><SignOut size={12} /> Logout everywhere</button>
                          )}
                        </div>
                        {(sessions[u.user_id] || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground">No active sessions</p>
                        ) : (
                          <div className="space-y-1.5">
                            {sessions[u.user_id].map(s => (
                              <div key={s.session_id} className="flex items-center justify-between text-xs rounded-lg bg-card border border-border px-3 py-2">
                                <span>{deviceLabel(s.device)} · IP {s.ip || '?'} · last seen {(s.last_seen || '').slice(0, 16).replace('T', ' ')}</span>
                                <button onClick={() => act(() => api.delete(`/auth/sessions/${s.session_id}`))} data-testid={`revoke-session-${s.session_id}`}
                                  className="text-red-500 hover:underline font-medium">Revoke</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">New sign-ups (including Google) wait here for your approval before they can log in. Disabling an account logs it out of every device instantly.</p>

      {/* Login activity modal */}
      {showActivity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="login-activity-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowActivity(false)} />
          <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold flex items-center gap-2"><ClockCounterClockwise size={18} /> Recent Login Activity</h3>
              <button onClick={() => setShowActivity(false)} data-testid="login-activity-close" className="p-1 rounded-lg hover:bg-muted"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 space-y-1.5" data-testid="login-activity-list">
              {activityLoading ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
              ) : activity.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No sign-ins recorded yet. History starts from the next login.</p>
              ) : activity.map(a => (
                <div key={a.login_id} data-testid={`login-event-${a.login_id}`}
                  className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-xs ${(a.new_device || a.new_ip) ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-muted/20'}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{a.name || a.email}
                      {a.new_device && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[9px] font-bold uppercase inline-flex items-center gap-1"><Warning size={9} weight="bold" /> New device</span>}
                      {!a.new_device && a.new_ip && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[9px] font-bold uppercase inline-flex items-center gap-1"><Warning size={9} weight="bold" /> New IP</span>}
                    </p>
                    <p className="text-muted-foreground truncate">{a.device_sig} · IP {a.ip || '?'} · via {a.method}</p>
                  </div>
                  <span className="text-muted-foreground whitespace-nowrap flex-shrink-0">{(a.timestamp || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">You and all admins get an alert whenever someone signs in from a device or IP not seen before on their account.</p>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invite-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowInvite(false)} />
          <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold">{invited ? 'Account Created' : 'Invite User'}</h3>
              <button onClick={() => setShowInvite(false)} data-testid="invite-close" className="p-1 rounded-lg hover:bg-muted"><X size={18} /></button>
            </div>
            {!invited ? (
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label className="overline block mb-2">Name</label>
                  <input type="text" value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} required
                    data-testid="invite-name-input" placeholder="e.g., Ramesh"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">Email</label>
                  <input type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} required
                    data-testid="invite-email-input" placeholder="their.email@example.com"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">Role</label>
                  <select value={inviteForm.role} onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })} data-testid="invite-role-select"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                    <option value="member">Member — regular use, expenses need your approval</option>
                    <option value="child">Child — restricted</option>
                    <option value="admin">Admin — full control</option>
                  </select>
                </div>
                <button type="submit" data-testid="send-invite-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">
                  Create Pre-Approved Account
                </button>
              </form>
            ) : (
              <div className="space-y-4" data-testid="invite-success">
                <p className="text-sm text-muted-foreground">Share these login details with <b>{invited.name}</b>. The password is shown <b>only once</b>.</p>
                <div className="rounded-xl bg-muted/40 border border-border px-4 py-3 space-y-1 text-sm">
                  <p><span className="text-muted-foreground">Email:</span> {invited.email}</p>
                  <p><span className="text-muted-foreground">Temporary password:</span> <span className="font-mono font-bold text-[#FF6B35]" data-testid="invite-temp-password">{invited.temp_password}</span></p>
                </div>
                <button onClick={copyInvite} data-testid="copy-invite-btn"
                  className="w-full py-3 rounded-full border border-border text-sm font-semibold hover:bg-muted flex items-center justify-center gap-2">
                  <Copy size={15} /> {copied ? 'Copied!' : 'Copy login details'}
                </button>
                <p className="text-[11px] text-muted-foreground">Ask them to change the password in Settings → General after first login.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
